import React, { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  Clock, 
  Database,
  Zap,
  AlertCircle,
  CheckCircle2,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  RefreshCw
} from 'lucide-react';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);

  // Performance Metrics
  const performanceMetrics = {
    avgMigrationTime: {
      value: '42 min',
      change: -12,
      trend: 'down',
      label: 'Avg Migration Time',
      icon: Clock
    },
    successRate: {
      value: '95.2%',
      change: 3.5,
      trend: 'up',
      label: 'Success Rate',
      icon: CheckCircle2
    },
    throughput: {
      value: '1.2M',
      change: 8.3,
      trend: 'up',
      label: 'Records/Hour',
      icon: Zap
    },
    activeConnections: {
      value: '42',
      change: 5,
      trend: 'up',
      label: 'Active Connections',
      icon: Activity
    }
  };

  // Migration by Database
  const migrationsByDatabase = [
    { name: 'Pinecone', total: 342, completed: 325, failed: 17, percentage: 27.4, color: 'bg-blue-500' },
    { name: 'Qdrant', total: 289, completed: 278, failed: 11, percentage: 23.2, color: 'bg-purple-500' },
    { name: 'Weaviate', total: 256, completed: 241, failed: 15, percentage: 20.5, color: 'bg-cyan-500' },
    { name: 'ChromaDB', total: 234, completed: 225, failed: 9, percentage: 18.8, color: 'bg-emerald-500' },
    { name: 'Milvus', total: 124, completed: 118, failed: 6, percentage: 10.1, color: 'bg-amber-500' }
  ];

  // Hourly Migration Volume (Last 24 hours)
  const hourlyVolume = [
    { hour: '00:00', count: 12 },
    { hour: '01:00', count: 8 },
    { hour: '02:00', count: 5 },
    { hour: '03:00', count: 3 },
    { hour: '04:00', count: 7 },
    { hour: '05:00', count: 15 },
    { hour: '06:00', count: 28 },
    { hour: '07:00', count: 42 },
    { hour: '08:00', count: 58 },
    { hour: '09:00', count: 72 },
    { hour: '10:00', count: 85 },
    { hour: '11:00', count: 78 },
    { hour: '12:00', count: 65 },
    { hour: '13:00', count: 70 },
    { hour: '14:00', count: 82 },
    { hour: '15:00', count: 76 },
    { hour: '16:00', count: 68 },
    { hour: '17:00', count: 55 },
    { hour: '18:00', count: 42 },
    { hour: '19:00', count: 35 },
    { hour: '20:00', count: 28 },
    { hour: '21:00', count: 22 },
    { hour: '22:00', count: 18 },
    { hour: '23:00', count: 15 }
  ];

  // Error Distribution
  const errorDistribution = [
    { type: 'Connection Timeout', count: 45, percentage: 38.5, color: 'bg-red-500' },
    { type: 'Authentication Failed', count: 32, percentage: 27.4, color: 'bg-orange-500' },
    { type: 'Rate Limit Exceeded', count: 18, percentage: 15.4, color: 'bg-amber-500' },
    { type: 'Data Format Error', count: 12, percentage: 10.3, color: 'bg-yellow-500' },
    { type: 'Network Error', count: 10, percentage: 8.5, color: 'bg-pink-500' }
  ];

  // Migration Trends (Last 7 days)
  const migrationTrends = [
    { day: 'Mon', total: 156, completed: 148, failed: 8 },
    { day: 'Tue', total: 182, completed: 175, failed: 7 },
    { day: 'Wed', total: 198, completed: 188, failed: 10 },
    { day: 'Thu', total: 165, completed: 157, failed: 8 },
    { day: 'Fri', total: 210, completed: 201, failed: 9 },
    { day: 'Sat', total: 145, completed: 138, failed: 7 },
    { day: 'Sun', total: 189, completed: 180, failed: 9 }
  ];

  // Top Migration Routes
  const topRoutes = [
    { source: 'Pinecone', destination: 'Qdrant', count: 145, avgTime: '38 min', successRate: 95 },
    { source: 'ChromaDB', destination: 'Weaviate', count: 132, avgTime: '42 min', successRate: 92 },
    { source: 'Qdrant', destination: 'Pinecone', count: 128, avgTime: '35 min', successRate: 89 },
    { source: 'Weaviate', destination: 'ChromaDB', count: 98, avgTime: '45 min', successRate: 86 },
    { source: 'Pinecone', destination: 'Weaviate', count: 87, avgTime: '40 min', successRate: 83 }
  ];

  const maxHourlyCount = Math.max(...hourlyVolume.map(h => h.count));

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />;
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-emerald-400' : 'text-red-400';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Analytics</h1>
          <p className="text-gray-400 mt-1">Detailed insights into your migration performance</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Time Range Selector */}
          <div className="flex gap-2">
            {['24h', '7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-[#141414] text-gray-400 hover:bg-gray-800 border border-gray-800'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
          <button 
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-gray-400 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-gray-400 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors">
            <Download size={18} />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.entries(performanceMetrics).map(([key, metric]) => {
          const Icon = metric.icon;
          return (
            <div
              key={key}
              className="bg-[#141414] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <Icon className="w-5 h-5 text-gray-500" />
                <span className={`flex items-center gap-1 text-xs font-medium ${getTrendColor(metric.trend)}`}>
                  {getTrendIcon(metric.trend)}
                  {Math.abs(metric.change)}%
                </span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{metric.value}</div>
              <div className="text-sm text-gray-400">{metric.label}</div>
            </div>
          );
        })}
      </div>

      {/* Migration Volume Chart */}
      <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Migration Volume</h3>
            <p className="text-sm text-gray-400 mt-1">Hourly migration count (Last 24 hours)</p>
          </div>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            <span className="text-sm text-gray-400">Peak: 85 migrations/hour</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[600px] flex items-end gap-1 h-64">
            {hourlyVolume.map((item, index) => {
              const height = (item.count / maxHourlyCount) * 100;
              return (
                <div key={index} className="flex-1 flex flex-col items-center group relative">
                  <div
                    className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all duration-300 hover:from-blue-500 hover:to-blue-300 cursor-pointer"
                    style={{ height: `${height}%` }}
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      {item.count} migrations
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-500 mt-2 transform -rotate-45 origin-top-left whitespace-nowrap">
                    {item.hour}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Migration Trends */}
        <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">7-Day Migration Trend</h3>
          <div className="space-y-4">
            {migrationTrends.map((day, index) => {
              const successRate = ((day.completed / day.total) * 100).toFixed(1);
              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400 font-medium w-12">{day.day}</span>
                    <span className="text-sm text-white">{day.total} total</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-800 rounded-full h-3 overflow-hidden">
                      <div className="flex h-full">
                        <div
                          className="bg-emerald-500"
                          style={{ width: `${(day.completed / day.total) * 100}%` }}
                        />
                        <div
                          className="bg-red-500"
                          style={{ width: `${(day.failed / day.total) * 100}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-emerald-400 min-w-[60px] text-right">
                      {successRate}%
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs">
                    <span className="text-emerald-400">✓ {day.completed}</span>
                    <span className="text-red-400">✗ {day.failed}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Migrations by Database */}
        <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Migrations by Database</h3>
          <div className="space-y-4">
            {migrationsByDatabase.map((db, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${db.color}`} />
                    <span className="text-sm text-white font-medium">{db.name}</span>
                  </div>
                  <span className="text-sm text-gray-400">{db.total} total</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                    <div
                      className={`${db.color} h-full rounded-full transition-all duration-500`}
                      style={{ width: `${db.percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 min-w-[45px] text-right">
                    {db.percentage}%
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs">
                  <span className="text-emerald-400">✓ {db.completed}</span>
                  <span className="text-red-400">✗ {db.failed}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Migration Routes */}
      <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-6">Top Migration Routes</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Source</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-400 w-12">→</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Destination</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Migrations</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Avg Time</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Success Rate</th>
              </tr>
            </thead>
            <tbody>
              {topRoutes.map((route, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-white font-medium">{route.source}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <Zap className="w-4 h-4 text-amber-400 mx-auto" />
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-white font-medium">{route.destination}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-white font-medium">{route.count}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <Clock className="w-3 h-3" />
                      {route.avgTime}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-800 rounded-full h-1.5 max-w-[100px]">
                        <div
                          className="bg-emerald-500 h-full rounded-full"
                          style={{ width: `${route.successRate}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 min-w-[40px]">{route.successRate}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Distribution */}
      <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Error Distribution</h3>
            <p className="text-sm text-gray-400 mt-1">Most common migration errors</p>
          </div>
          <AlertCircle className="w-5 h-5 text-red-400" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {errorDistribution.map((error, index) => (
            <div
              key={index}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:border-red-500/50 transition-colors"
            >
              <div className="text-2xl font-bold text-white mb-1">{error.count}</div>
              <div className="text-xs text-gray-400 mb-3 h-8">{error.type}</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-700 rounded-full h-1.5">
                  <div
                    className={`${error.color} h-full rounded-full transition-all duration-500`}
                    style={{ width: `${error.percentage}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">{error.percentage}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">2,450</div>
              <div className="text-sm text-gray-400">Total Migrations</div>
            </div>
          </div>
          <div className="text-xs text-blue-400 flex items-center gap-1">
            <ArrowUpRight size={12} />
            12.5% from last period
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">2,332</div>
              <div className="text-sm text-gray-400">Successful</div>
            </div>
          </div>
          <div className="text-xs text-emerald-400">95.2% success rate</div>
        </div>

        <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">118</div>
              <div className="text-sm text-gray-400">Failed</div>
            </div>
          </div>
          <div className="text-xs text-red-400 flex items-center gap-1">
            <ArrowDownRight size={12} />
            3.2% from last period
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;